import React, { useCallback, useMemo } from 'react';
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import {
  KpiCards,
  KpiRateDonuts,
  KpiPrioritySlaChart,
  KpiSlaByTypeBarChart,
  KPI_SLA_METRIC_COLORS,
  KpiMonthlyTrendChart,
  type KpiSlaBreachBarGroup,
} from './charts';
import type { KpiData } from './types';
import type { KpiDrilldownSpec } from './kpiDashboardApi';
import KpiOpenBacklogSection from './KpiOpenBacklogSection';
import { formatKpiRatePercentFromNumberString, formatKpiTwoDecimalDisplay } from './kpiDashboardNumberFormat';

export interface KpiDashboardContentProps {
  data: KpiData | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  onRetry?: () => void;
  onDrill: (spec: KpiDrilldownSpec) => void;
  filterValues: Record<string, string | string[]>;
  /** From TanStack `dataUpdatedAt` — suffix chart `key`s so Chart.js remounts on new data (must differ per sibling; never reuse raw `0` alone). */
  chartsRenderRevision: number;
}

export function SectionLabel({
  children,
  id,
}: {
  children: React.ReactNode;
  /** Optional stable id for `aria-labelledby` on a wrapping `<section>`. */
  id?: string;
}) {
  return (
    <div className="mb-3 mt-1 flex items-center gap-3">
      <span
        className="h-1 w-8 shrink-0 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500"
        aria-hidden
      />
      <h2 id={id} className="text-xs font-semibold uppercase tracking-wider text-slate-500">
        {children}
      </h2>
      <span className="h-px min-w-[2rem] flex-1 bg-slate-200/90" aria-hidden />
    </div>
  );
}

function KpiDashboardSkeleton() {
  return (
    <div className="space-y-8" aria-busy="true" aria-label="Loading dashboard">
      <div>
        <div className="mb-3 h-3 w-40 animate-pulse rounded bg-slate-200" />
        <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4 lg:grid-cols-7">
          {Array.from({ length: 7 }).map((_, i) => (
            <div
              key={i}
              className="h-28 animate-pulse rounded-xl border border-slate-200/80 bg-gradient-to-br from-slate-100 to-slate-50 shadow-sm"
            />
          ))}
        </div>
      </div>
      <div>
        <div className="mb-3 h-3 w-48 animate-pulse rounded bg-slate-200" />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="h-72 animate-pulse rounded-xl border border-slate-200/80 bg-slate-100/80 shadow-sm" />
          <div className="h-72 animate-pulse rounded-xl border border-slate-200/80 bg-slate-100/80 shadow-sm" />
        </div>
      </div>
      <div>
        <div className="mb-3 h-3 w-56 animate-pulse rounded bg-slate-200" />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="h-80 animate-pulse rounded-xl border border-slate-200/80 bg-slate-100/80 shadow-sm" />
          <div className="h-80 animate-pulse rounded-xl border border-slate-200/80 bg-slate-100/80 shadow-sm" />
        </div>
      </div>
    </div>
  );
}

const KpiDashboardContent: React.FC<KpiDashboardContentProps> = ({
  data,
  isLoading,
  isFetching,
  error,
  onRetry,
  onDrill,
  filterValues,
  chartsRenderRevision,
}) => {
  const openMetric = useCallback(
    (key: string) => {
      onDrill({ kind: 'metric', key });
    },
    [onDrill]
  );

  const openRate = useCallback(
    (key: string) => {
      onDrill({ kind: 'rate', key });
    },
    [onDrill]
  );

  const openPriority = useCallback(
    (priority: string) => {
      onDrill({ kind: 'priority', priority });
    },
    [onDrill]
  );

  const openSlaMetricBreached = useCallback(
    (rateBreachedDrillKey: string) => {
      onDrill({ kind: 'rate', key: rateBreachedDrillKey });
    },
    [onDrill]
  );

  const slaBreachGroups = useMemo((): KpiSlaBreachBarGroup[] => {
    if (!data) return [];
    const m = data.slaBreachBySlaMetric;
    return [
      {
        axisLabel: 'Case',
        segments: [
          {
            label: 'Case — Response SLA',
            value: m.caseResponse,
            color: KPI_SLA_METRIC_COLORS.case,
            rateBreachedDrillKey: 'case_response_breached',
          },
        ],
      },
      {
        axisLabel: 'Incident',
        segments: [
          {
            label: 'Incident — Ongoing Comm SLA',
            value: m.incidentOngoing,
            color: KPI_SLA_METRIC_COLORS.ongoingComm,
            rateBreachedDrillKey: 'incident_ongoing_breached',
          },
          {
            label: 'Incident — Resolution SLA',
            value: m.incidentResolution,
            color: KPI_SLA_METRIC_COLORS.resolution,
            rateBreachedDrillKey: 'incident_resolution_breached',
          },
        ],
      },
      {
        axisLabel: 'Request',
        segments: [
          {
            label: 'Request — Ongoing Comm SLA',
            value: m.requestOngoing,
            color: KPI_SLA_METRIC_COLORS.ongoingComm,
            rateBreachedDrillKey: 'request_ongoing_breached',
          },
          {
            label: 'Request — Resolution SLA',
            value: m.requestResolution,
            color: KPI_SLA_METRIC_COLORS.resolution,
            rateBreachedDrillKey: 'request_resolution_breached',
          },
        ],
      },
    ];
  }, [data]);

  const openTrend = useCallback(
    (monthIndex: number, series: 'Case' | 'Incident' | 'Request') => {
      onDrill({ kind: 'trend', monthIndex, series });
    },
    [onDrill]
  );
  if (isLoading && !data) {
    return <KpiDashboardSkeleton />;
  }

  if (error && !data) {
    return (
      <div className="rounded-xl border border-red-200/80 bg-gradient-to-br from-red-50 to-white p-8 text-center shadow-sm shadow-red-900/5">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
          <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden />
        </div>
        <p className="mt-4 text-sm font-semibold text-red-900">Could not load KPI dashboard data</p>
        <p className="mt-1 text-sm text-red-700/90">{error.message}</p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-5 inline-flex items-center justify-center rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          >
            Try again
          </button>
        )}
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const cardItems = [
    { title: 'Tickets Total Count KPI', value: data.ticketsTotal.toLocaleString(), drillKey: 'tickets_total' },
    { title: 'Incidents Volume Count KPI', value: data.incidentsVolume.toLocaleString(), drillKey: 'incidents_volume' },
    { title: 'Requests Volume Total KPI', value: data.requestsVolume.toLocaleString(), drillKey: 'requests_volume' },
    { title: 'Cases Volume Total KPI', value: data.casesVolume.toLocaleString(), drillKey: 'cases_volume' },
    { title: 'MTTR (Minutes)', value: formatKpiTwoDecimalDisplay(data.mttrMinutes), drillKey: 'mttr' },
    {
      title: 'Change Failure Rate',
      value: formatKpiRatePercentFromNumberString(
        String(data.changeFailureRate).includes('%') ? String(data.changeFailureRate) : `${data.changeFailureRate}%`
      ),
      drillKey: 'change_failure',
    },
    { title: 'Tickets Avg Open Days', value: formatKpiTwoDecimalDisplay(data.ticketsAvgOpenDays), drillKey: 'avg_open_days' },
  ];

  const fmtRate = (v: string) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n.toFixed(2) : v;
  };
  const rateItems = [
    {
      title: 'Case — Response SLA',
      value: fmtRate(data.caseResponseSlaMetRate),
      ticketCount: data.caseResponseSlaTicketCount,
      drillKey: 'case_response',
    },
    {
      title: 'Incident — Ongoing Comm SLA',
      value: fmtRate(data.incidentOngoingSlaMetRate),
      ticketCount: data.incidentOngoingSlaTicketCount,
      drillKey: 'incident_ongoing',
    },
    {
      title: 'Incident Resolution SLA',
      value: fmtRate(data.incidentResolutionSlaMetRate),
      ticketCount: data.incidentResolutionSlaTicketCount,
      drillKey: 'incident_resolution',
    },
    {
      title: 'Request — Ongoing Comm SLA',
      value: fmtRate(data.requestOngoingSlaMetRate),
      ticketCount: data.requestOngoingSlaTicketCount,
      drillKey: 'request_ongoing',
    },
    {
      title: 'Request Resolution SLA',
      value: fmtRate(data.requestResolutionSlaMetRate),
      ticketCount: data.requestResolutionSlaTicketCount,
      drillKey: 'request_resolution',
    },
  ];

  const trendSeries = [
    {
      label: 'Case',
      data: data.monthlyTrendCase,
      borderColor: 'rgb(30, 64, 175)',
      backgroundColor: 'rgba(30, 64, 175, 0.12)',
    },
    {
      label: 'Incident',
      data: data.monthlyTrendIncident,
      borderColor: 'rgb(217, 119, 6)',
      backgroundColor: 'rgba(217, 119, 6, 0.12)',
    },
    {
      label: 'Request',
      data: data.monthlyTrendRequest,
      borderColor: 'rgb(147, 51, 234)',
      backgroundColor: 'rgba(147, 51, 234, 0.1)',
    },
  ];

  return (
    <div className="relative">
      {isFetching && (
        <div
          className="pointer-events-none absolute right-0 top-0 z-10 flex items-center gap-1.5 rounded-full border border-indigo-200/80 bg-white/95 px-3 py-1.5 text-xs font-medium text-indigo-800 shadow-md shadow-indigo-900/10 backdrop-blur-sm"
          aria-live="polite"
        >
          <ArrowPathIcon className="h-3.5 w-3.5 animate-spin text-indigo-600" aria-hidden />
          Updating
        </div>
      )}
      {error && data && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-amber-200/90 bg-amber-50/90 px-4 py-3 text-sm text-amber-950 shadow-sm">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-600" aria-hidden />
          <span className="min-w-0 flex-1">
            Showing cached data. Latest refresh failed: {error.message}
          </span>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="shrink-0 font-semibold text-amber-900 underline decoration-amber-600/50 underline-offset-2 hover:text-amber-950"
            >
              Retry
            </button>
          )}
        </div>
      )}

      <section className="mb-8">
        <SectionLabel>Key metrics</SectionLabel>
        <KpiCards items={cardItems} onDrillMetric={openMetric} />
      </section>

      <section className="mb-8">
        <SectionLabel>Open backlog tables</SectionLabel>
        <KpiOpenBacklogSection
          ageingByGroup={data.operationalAgeingByGroup}
          lastUpdatedByGroup={data.lastUpdatedByGroup}
          filterValues={filterValues}
        />
      </section>

      <section className="mb-8">
        <SectionLabel>Rates, SLA &amp; priority</SectionLabel>
        <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-2">
          {/* Unique keys per widget: same numeric dataUpdatedAt (e.g. 0) on all siblings breaks reconciliation and duplicates UI */}
          <KpiRateDonuts key={`kpi-rates-${chartsRenderRevision}`} items={rateItems} onRateDrill={openRate} />
          <KpiPrioritySlaChart
            key={`kpi-priority-sla-${chartsRenderRevision}`}
            title="Tickets Distribution Priority Level w/ SLA Breach %"
            priorityCounts={data.priorityCounts}
            slaBreachPct={data.slaBreachPct}
            onPriorityBarClick={openPriority}
          />
        </div>
      </section>

      <section>
        <SectionLabel>Distribution &amp; trends</SectionLabel>
        <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-2">
          <KpiSlaByTypeBarChart
            key={`kpi-sla-by-type-${chartsRenderRevision}`}
            title="Tickets SLA breach counts by SLA metric"
            groups={slaBreachGroups}
            onBarClick={openSlaMetricBreached}
          />
          <KpiMonthlyTrendChart
            key={`kpi-monthly-trend-${chartsRenderRevision}`}
            title="Tickets Monthly Trend By Type"
            series={trendSeries}
            monthLabels={data.monthlyTrendMonthLabels}
            onPointClick={openTrend}
          />
        </div>
      </section>
    </div>
  );
};

export default KpiDashboardContent;
